import { GATEWAY_CONFIG } from '../config/config';
import { ONE_SIZE_IDS } from '../config/dataMapping';
import { getAnonymousId } from './cacheService';
import logger from './logger';

/**
 * Service dédié à la recommandation de taille via la Gateway
 */

const toGatewayGender = (gender) => (gender === 'homme' ? 'H' : 'F');

// Mapping label backend → clé interne
const BACKEND_LABEL_MAP = {
  'Idéal':                'ideal',
  'Ajusté':               'adjusted',
  'Légèrement Oversize':  'slightlyOversize',
  'Très ajusté':          'tightFit',
  'Oversize':             'oversize',
  'Trop petit':           'tooSmall',
  'Trop grand':           'tooLarge',
};

// Couleurs hex par clé (symétrie rouge ↔ bleu)
const LABEL_COLOR_MAP = {
  ideal:           '#289e53',  // vert foncé
  adjusted:        '#4ade80',  // vert clair
  slightlyOversize:'#4ade80',  // vert clair
  tightFit:        '#f59e0b',  // orange
  oversize:        '#93c5fd',  // bleu clair
  tooSmall:        '#ef4444',  // rouge
  tooLarge:        '#1e3a8a',  // bleu foncé
};

// Fallback si le label n'est pas reconnu
const BACKEND_COLOR_MAP = {
  vert:   '#22c55e',
  orange: '#f59e0b',
  rouge:  '#ef4444',
};

const PRODUCT_TYPE_MAP = {
  'Haut':     'top',
  'Bas':      'bottom',
  'Ensemble': 'full',
};

// 'shoulder' est volontairement exclu : toujours calculé côté backend mais plus affiché ni
// déterminant pour la taille recommandée.
const KNOWN_ZONES = new Set(['chest', 'waist', 'hips']);

const adaptFitColors = (fitColors) => {
  if (!fitColors) return null;
  return Object.entries(fitColors)
    .filter(([zone]) => KNOWN_ZONES.has(zone))
    .map(([zone, value]) => {
      const colorName = Array.isArray(value) ? value[0] : value;
      const labelText = Array.isArray(value) ? value[1] : null;
      const labelKey = labelText ? (BACKEND_LABEL_MAP[labelText] || null) : null;
      return {
        zone,
        color: (labelKey && LABEL_COLOR_MAP[labelKey]) || BACKEND_COLOR_MAP[colorName] || BACKEND_COLOR_MAP.vert,
        labelKey,
      };
    });
};

export const adaptRecommendationResponse = (backendResponse) => {
  if (!backendResponse) throw new Error('Réponse de recommandation vide');

  const recommendation = backendResponse.recommendation;
  if (!Array.isArray(recommendation) || recommendation.length === 0) {
    throw new Error('Liste de recommandations vide');
  }

  const sizes = [...recommendation]
    .sort((a, b) => (a.size?.size_order ?? 0) - (b.size?.size_order ?? 0))
    .map((entry) => ({
      size:          entry.size?.size_label || null,
      sizeId:        entry.size?.id || null,
      stockQuantity: entry.stock_quantity ?? null,
      isAvailable:   typeof entry.is_available === 'boolean' ? entry.is_available : null,
      fitZones:      adaptFitColors(entry.fit_colors),
      isRecommended:    !!entry.is_recommended,
      isUncertain:      !!entry.is_uncertain,
      isNotRecommended: !!entry.is_not_recommended,
      isIdealMatch:     !!entry.is_ideal_match,
      fallbackDirection: entry.fallback_direction || null,
    }));

  const recommendedIndex = sizes.findIndex((s) => s.isRecommended);
  const effectiveRecommendedIndex = recommendedIndex >= 0 ? recommendedIndex : 0;
  const recommendedSizeId = sizes[effectiveRecommendedIndex]?.sizeId;
  const isOneSize = sizes.length === 1 && !!recommendedSizeId && ONE_SIZE_IDS.has(recommendedSizeId);
  const garmentType = PRODUCT_TYPE_MAP[backendResponse.product_type] || null;

  logger.log('📊 Adaptation réponse recommandation:', {
    count: sizes.length,
    recommendedIndex: effectiveRecommendedIndex,
    garmentType,
    isOneSize,
  });

  return {
    isOneSize,
    sizes,
    recommendedIndex: effectiveRecommendedIndex,
    garmentType,
    recommendationId: backendResponse.recommendation_id || null,
  };
};

/**
 * Calcule le profil physique complet via la gateway.
 * Doit être appelé avant getSizeRecommendationAnonymous et generateVTOAnonymous.
 */
export const computeProfile = async ({
  gender, height, weight, age,
  deltaChest, deltaWaist, deltaHips,
  manualChest, manualWaist, manualHips,
}) => {
  try {
    logger.log('📤 Calcul du profil physique...');

    const response = await fetch(
      `${GATEWAY_CONFIG.BASE_URL}/api/profile/compute`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          gender: toGatewayGender(gender),
          height: parseInt(height),
          weight: parseInt(weight),
          age: age ? parseInt(age) : null,
          delta_chest: deltaChest ?? 0,
          delta_waist: deltaWaist ?? 0,
          delta_hips:  deltaHips  ?? 0,
          manual_chest: manualChest ?? null,
          manual_waist: manualWaist ?? null,
          manual_hips:  manualHips  ?? null,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.details || `Erreur HTTP: ${response.status}`);
    }

    const measurements = await response.json();
    logger.log('✅ Profil physique calculé:', measurements);
    return measurements;
  } catch (error) {
    logger.error('❌ Erreur calcul profil physique:', error);
    throw error;
  }
};

export const getSizeRecommendationAnonymous = async ({
  customerDomain,
  productId,
  gender,
  height,
  weight,
  age,
  computedMeasurements,
  sessionId,
}) => {
  try {
    if (!customerDomain || !productId) throw new Error('customerDomain et productId requis');
    if (!computedMeasurements) throw new Error('computedMeasurements requis — appeler computeProfile() avant');

    logger.log('📤 Envoi requête recommandation de taille anonymous via gateway...');

    const response = await fetch(
      `${GATEWAY_CONFIG.BASE_URL}${GATEWAY_CONFIG.ENDPOINTS.SIZE_RECO_ANONYMOUS}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_domain:  customerDomain,
          legacy_product_id: productId,
          user_identifier:  getAnonymousId(),
          gender:           toGatewayGender(gender),
          height:           parseInt(height),
          weight:           parseInt(weight),
          age:              age ? parseInt(age) : null,
          session_id:       sessionId,
          ...computedMeasurements,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (errorData.error_code) throw new Error(errorData.error_code);
      if (errorData.details) throw new Error(errorData.details);
      if (response.status === 400) throw new Error('PRODUCT_NOT_READY');
      throw new Error(`Erreur HTTP: ${response.status}`);
    }

    const rawData = await response.json();
    logger.log('✅ Recommandation anonymous reçue:', rawData);
    return adaptRecommendationResponse(rawData);
  } catch (error) {
    logger.error('❌ Erreur recommandation anonymous (gateway):', error);
    throw error;
  }
};

export const notifyAddToCart = async ({ customerDomain, recommendationId, selectedSizeId }) => {
  try {
    logger.log('📤 Notification ajout au panier via gateway...');

    const response = await fetch(
      `${GATEWAY_CONFIG.BASE_URL}${GATEWAY_CONFIG.ENDPOINTS.ADD_TO_CART}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_domain:   customerDomain,
          recommendation_id: recommendationId,
          selected_size_id:  selectedSizeId,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.details || `Erreur HTTP: ${response.status}`);
    }

    const data = await response.json();
    logger.log('✅ Notification ajout au panier envoyée');
    return data;
  } catch (error) {
    logger.error('❌ Erreur notification ajout au panier (gateway):', error);
    throw error;
  }
};

export const getSizeRecommendationShopper = async ({
  customerDomain,
  productId,
  userIdentifier,
  firebaseToken,
  sessionId,
}) => {
  try {
    if (!userIdentifier || !firebaseToken) throw new Error('userIdentifier et firebaseToken requis');
    if (!customerDomain || !productId) throw new Error('customerDomain et productId requis');

    logger.log('📤 Envoi requête recommandation de taille shopper via gateway...');

    const response = await fetch(
      `${GATEWAY_CONFIG.BASE_URL}${GATEWAY_CONFIG.ENDPOINTS.SIZE_RECO_SHOPPER}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_domain:   customerDomain,
          legacy_product_id: productId,
          user_identifier:   userIdentifier,
          firebase_token:    firebaseToken,
          session_id:        sessionId,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (errorData.error_code) throw new Error(errorData.error_code);
      if (errorData.details) throw new Error(errorData.details);
      if (response.status === 400) throw new Error('PRODUCT_NOT_READY');
      throw new Error(`Erreur HTTP: ${response.status}`);
    }

    const rawData = await response.json();
    logger.log('✅ Recommandation shopper reçue:', rawData);
    return adaptRecommendationResponse(rawData);
  } catch (error) {
    logger.error('❌ Erreur recommandation shopper (gateway):', error);
    throw error;
  }
};
